export const ROLES = ["admin", "editor"] as const;
export type Role = (typeof ROLES)[number];

/** Roles inside an IT partner company (issue #27). */
export const PARTNER_ROLES = ["admin", "technician"] as const;
export type PartnerRole = (typeof PARTNER_ROLES)[number];
export type PartnerLinkStatus = "pending" | "active";
/** Who pays for a customer organization linked to an IT partner. */
export const BILLED_TO = ["customer", "partner"] as const;
export type BilledTo = (typeof BILLED_TO)[number];
/** A partner's application for paying by monthly invoice instead of card (an operator decides). */
export type PartnerInvoiceStatus = "none" | "requested" | "approved" | "rejected";

/**
 * Where delivered contacts live in a mailbox.
 * "folder": one dedicated contact folder, separate from personal contacts.
 * "main": the default Contacts folder (needed if phones only sync that folder), labelled with categories.
 */
export const DELIVERY_MODES = ["folder", "main"] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

export const LANGUAGES = ["de", "en"] as const;
export type Language = (typeof LANGUAGES)[number];

export const RECIPIENT_STATUSES = ["pending", "syncing", "ok", "error", "unreachable", "removing"] as const;
export type RecipientStatus = (typeof RECIPIENT_STATUSES)[number];

/** Machine-readable reasons why a person could not be reached. The UI translates them. */
export const RECIPIENT_REASONS = [
  "no_mailbox",
  "out_of_scope",
  "consent_missing",
  "throttled",
  "guest",
  /** A run was stopped; the mailbox continues on "sync now", a contact change or the nightly pass. */
  "cancelled",
  "unknown",
] as const;
export type RecipientReason = (typeof RECIPIENT_REASONS)[number];

export const CONNECTION_STATUSES = ["none", "granted", "error"] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

/**
 * How Kontaktfix reaches the tenant.
 * "consent": the customer approves the Kontaktfix multi-tenant app (one click).
 * "own_app": the customer registers their own app and gives Kontaktfix its client ID and credential.
 */
export const CONNECTION_METHODS = ["consent", "own_app"] as const;
export type ConnectionMethod = (typeof CONNECTION_METHODS)[number];

/** Credential of a customer-owned app: a certificate generated by Kontaktfix, or a client secret. */
export const OWN_APP_CREDENTIAL_KINDS = ["certificate", "secret"] as const;
export type OwnAppCredentialKind = (typeof OWN_APP_CREDENTIAL_KINDS)[number];

/** What the setup check of a customer-owned app verifies, in order. */
export const OWN_APP_CHECKS = ["credential", "contacts", "users", "groups"] as const;
export type OwnAppCheckKey = (typeof OWN_APP_CHECKS)[number];

export const LIST_COLORS = [
  "gray",
  "iris",
  "blue",
  "teal",
  "green",
  "amber",
  "orange",
  "red",
  "pink",
  "violet",
] as const;
export type ListColor = (typeof LIST_COLORS)[number];

export const ASSIGNMENT_TARGETS = ["user", "group"] as const;
export type AssignmentTarget = (typeof ASSIGNMENT_TARGETS)[number];

export const SYNC_TRIGGERS = ["change", "manual", "schedule", "directory", "setup", "disconnect"] as const;
export type SyncTrigger = (typeof SYNC_TRIGGERS)[number];

/** Kontaktfix operator accounts. "invited": setup link sent, password and authenticator not set yet. */
export const PLATFORM_ADMIN_STATUSES = ["invited", "active", "disabled"] as const;
export type PlatformAdminStatus = (typeof PLATFORM_ADMIN_STATUSES)[number];

/**
 * Lifecycle of a sent email. "logged": no Resend key configured, only written to the log.
 * delivered, delayed, bounced, complained and suppressed come from Resend webhooks.
 */
export const EMAIL_STATUSES = [
  "sending",
  "sent",
  "logged",
  "failed",
  "delivered",
  "delayed",
  "bounced",
  "complained",
  "suppressed",
] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

/** Subscription status as Stripe reports it. */
export const SUBSCRIPTION_STATUSES = [
  "incomplete",
  "incomplete_expired",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "unpaid",
  "paused",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/**
 * Where an organization stands with paying.
 * "disabled": billing is not configured on this server (development, self-hosting): everything works.
 * "trial_pending": nothing delivered yet, the trial has not started.
 * "trial": within the free trial.
 * "active" / "past_due": subscribed ("past_due": a payment failed, Stripe retries; it keeps working).
 * "expired": the trial ended without a subscription. "canceled": the subscription ended.
 * In "expired" and "canceled" nothing is delivered and contacts cannot be created or edited.
 */
export const BILLING_STATES = [
  "disabled",
  "trial_pending",
  "trial",
  "active",
  "past_due",
  "expired",
  "canceled",
] as const;
export type BillingState = (typeof BILLING_STATES)[number];

/** List price. Billing amounts come from the Stripe price; these values are for display. */
/** Stripe price lookup key in the zuvo form <slug>_<plan>_<interval>. */
export const PRICE_LOOKUP_KEY = "kontaktfix_kontaktfix_monthly";
/** The key billing:setup used before the zuvo standard; still found so no second price is created. */
export const LEGACY_PRICE_LOOKUP_KEY = "kontaktfix_user_monthly_chf";

export const PRICING = {
  /** Plan id in zuvo.yaml, used in billing events. */
  plan: "kontaktfix",
  interval: "monthly",
  currency: "CHF",
  /** Per user who receives contacts, per month. */
  pricePerUser: 2,
  trialDays: 15,
  /** From this many users, prices are on request. */
  volumeFromUsers: 500,
} as const;
